/**
 * Geocoding service using backend proxy for Nominatim
 * All requests go through our backend to handle CORS, rate limiting, and caching
 */

import { Geolocation } from '@capacitor/geolocation';

export interface GeocodeSuggestion {
  display_name: string;
  street_number?: string;
  street_name?: string;
  street_address: string;
  city: string;
  district?: string; // Neighborhood, suburb, or arrondissement
  state?: string;
  postal_code?: string;
  country: string;
  latitude: number;
  longitude: number;
  precision: 'exact' | 'street' | 'district' | 'city';
  bbox?: [number, number, number, number]; // [minLon, minLat, maxLon, maxLat]
}

/**
 * Search addresses using optimized backend geocoding service
 * This uses the same intelligent backend service as Map.tsx with:
 * - Multi-provider support (Photon + Nominatim)
 * - Smart caching
 * - Fuzzy matching for typos
 * - Geographic prioritization with user location
 *
 * @param query Search string
 * @param limit Number of results (default: 5)
 * @param userLat Optional user latitude for geographic prioritization
 * @param userLon Optional user longitude for geographic prioritization
 * @param mode Search mode: 'standard' (Photon only) or 'high-quality' (Photon + Nominatim)
 * @returns Array of suggestions sorted by relevance
 */
export async function searchAddresses(
  query: string,
  limit: number = 5,
  userLat?: number,
  userLon?: number,
  mode: 'standard' | 'high-quality' = 'standard'
): Promise<GeocodeSuggestion[]> {
  if (!query || query.trim().length < 3) {
    return [];
  }

  try {
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

    // Build URL with geographic bias if available
    let searchUrl = `${apiBaseUrl}/search/places?q=${encodeURIComponent(query.trim())}&limit=${limit}&mode=${mode}`;

    if (userLat !== undefined && userLon !== undefined) {
      searchUrl += `&lat=${userLat}&lon=${userLon}&radius=50`;
    }

    const response = await fetch(searchUrl);

    if (!response.ok) {
      throw new Error(`Geocoding API error: ${response.status}`);
    }

    const data = await response.json();

    // Convert backend results to GeocodeSuggestion format
    return data.map((item: any) => parseBackendResult(item));
  } catch (error) {
    console.error('Autocomplete search error:', error);
    return [];
  }
}

/**
 * Reverse geocode coordinates to address using backend proxy
 * The backend handles Nominatim API calls with proper User-Agent, rate limiting, and caching
 * @param latitude Latitude
 * @param longitude Longitude
 * @returns Address suggestion
 */
export async function reverseGeocode(
  latitude: number,
  longitude: number
): Promise<GeocodeSuggestion | null> {
  try {
    const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const url = `${apiBaseUrl}/search/reverse?lat=${latitude}&lon=${longitude}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Reverse geocoding API error: ${response.status}`);
    }

    const data = await response.json();
    return parseNominatimResult(data);
  } catch (error) {
    console.error('Reverse geocode error:', error);
    return null;
  }
}

/**
 * Parse backend geocoding service result
 */
function parseBackendResult(result: any): GeocodeSuggestion {
  const addr = result.address || {};

  // Extract address components (check both formats: house_number and housenumber)
  const housenumber = addr.house_number || addr.housenumber;
  // Check addr.road/street first, then fall back to result.name for street-type results
  let street = addr.road || addr.street || addr.pedestrian;

  // If no street in address but result is a street/road type, use the name field
  if (!street && result.name && (result.type === 'street' || result.class === 'highway')) {
    street = result.name;
  }

  const city = addr.city || addr.town || addr.village || addr.municipality || addr.county;
  const state = addr.state || addr.region;
  const postcode = addr.postcode;
  const country = addr.country;

  // Extract district (suburb, neighbourhood, city_district, or arrondissement for Paris)
  let district = addr.suburb || addr.neighbourhood || addr.district || addr.city_district;

  // Special handling for Paris arrondissements
  if (!district && postcode && postcode.startsWith('750') && city?.toLowerCase().includes('paris')) {
    const arrondissementNum = parseInt(postcode.slice(3, 5));
    if (arrondissementNum >= 1 && arrondissementNum <= 20) {
      const suffix = arrondissementNum === 1 ? 'er' : 'ème';
      district = `${arrondissementNum}${suffix} arrondissement`;
    }
  }

  // If no house number in address, try to extract from display_name
  let extractedNumber = housenumber;
  if (!extractedNumber && result.display_name) {
    const match = result.display_name.match(/^(\d+[\w]*)\s/);
    if (match) {
      extractedNumber = match[1];
    }
  }

  // Determine precision
  let precision: 'exact' | 'street' | 'district' | 'city' = 'city';
  if (extractedNumber) precision = 'exact';
  else if (street) precision = 'street';
  else if (district) precision = 'district';

  // Build street address
  let streetAddress = '';
  if (extractedNumber && street) {
    streetAddress = `${extractedNumber} ${street}`;
  } else if (street) {
    streetAddress = street;
  } else if (addr.suburb) {
    streetAddress = addr.suburb;
  } else if (result.name) {
    streetAddress = result.name;
  }

  return {
    display_name: result.display_name,
    street_number: extractedNumber || undefined,
    street_name: street || undefined,
    street_address: streetAddress,
    city: city || '',
    district: district || undefined,
    state: state || undefined,
    postal_code: postcode || undefined,
    country: country || '',
    latitude: parseFloat(result.lat),
    longitude: parseFloat(result.lon),
    precision,
  };
}

/**
 * Parse Nominatim API response
 */
function parseNominatimResult(result: any): GeocodeSuggestion | null {
  if (!result || result.error) {
    return null;
  }

  const addr = result.address || {};

  // Extract components
  const housenumber = addr.house_number;
  const street = addr.road || addr.street || addr.pedestrian;
  const city = addr.city || addr.town || addr.village || addr.municipality;
  const state = addr.state || addr.region || addr.county;
  const postcode = addr.postcode;
  const country = addr.country;

  // Extract district
  let district = addr.suburb || addr.neighbourhood || addr.district || addr.city_district;

  // Special handling for Paris arrondissements
  if (!district && postcode && postcode.startsWith('750') && city?.toLowerCase().includes('paris')) {
    const arrondissementNum = parseInt(postcode.slice(3, 5));
    if (arrondissementNum >= 1 && arrondissementNum <= 20) {
      const suffix = arrondissementNum === 1 ? 'er' : 'ème';
      district = `${arrondissementNum}${suffix} arrondissement`;
    }
  }

  // Determine precision
  let precision: 'exact' | 'street' | 'district' | 'city' = 'city';
  if (housenumber) precision = 'exact';
  else if (street) precision = 'street';
  else if (district) precision = 'district';

  // Build street address
  let streetAddress = '';
  if (housenumber && street) {
    streetAddress = `${housenumber} ${street}`;
  } else if (street) {
    streetAddress = street;
  } else if (addr.suburb) {
    streetAddress = addr.suburb;
  }

  return {
    display_name: result.display_name,
    street_number: housenumber || undefined,
    street_name: street || undefined,
    street_address: streetAddress,
    city: city || '',
    district: district || undefined,
    state: state || undefined,
    postal_code: postcode || undefined,
    country: country || '',
    latitude: parseFloat(result.lat),
    longitude: parseFloat(result.lon),
    precision,
  };
}

/**
 * Get user's current geolocation using Capacitor plugin
 * Works on both web (with HTTPS) and native mobile apps
 */
export async function getCurrentPosition(): Promise<{ latitude: number; longitude: number }> {
  try {
    // Request permission first (required on mobile)
    const permissionStatus = await Geolocation.checkPermissions();

    if (permissionStatus.location !== 'granted') {
      const requestResult = await Geolocation.requestPermissions();
      if (requestResult.location !== 'granted') {
        throw new Error('Geolocation permission denied');
      }
    }

    // Get current position using Capacitor
    const position = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 10000,
    });

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };
  } catch (error: any) {
    console.error('Geolocation error:', error);
    throw new Error(error.message || 'Failed to get location');
  }
}

/**
 * Simplify a verbose address to keep only essential components
 *
 * Example: "Théâtre du Châtelet, 1, Place du Châtelet, les Halles, Paris 1er Arrondissement,
 *           Paris, Île-de-france, France Métropolitaine, 75001, France"
 * Becomes: "1 Place du Châtelet, 75001 Paris, France"
 *
 * @param address The full address suggestion from geocoding
 * @returns A simplified, readable address string
 */
export function simplifyAddress(address: GeocodeSuggestion): string {
  const parts: string[] = [];

  // 1. Street address (number + street name if available)
  if (address.street_number && address.street_name) {
    parts.push(`${address.street_number} ${address.street_name}`);
  } else if (address.street_name) {
    parts.push(address.street_name);
  } else if (address.district) {
    // For less precise addresses, use district
    parts.push(address.district);
  }

  // 2. Postal code + City
  if (address.postal_code && address.city) {
    parts.push(`${address.postal_code} ${address.city}`);
  } else if (address.city) {
    parts.push(address.city);
  } else if (address.district && !parts.includes(address.district)) {
    // Use district as fallback if no city
    parts.push(address.district);
  }

  // 3. Country (always include)
  if (address.country) {
    parts.push(address.country);
  }

  // If we couldn't build a simplified address, fall back to display_name
  if (parts.length === 0) {
    return address.display_name;
  }

  return parts.join(', ');
}

/**
 * Analyze text to determine address completeness
 * Used to show dynamic precision indicator based on user's edited address
 *
 * @param text The address text from the textarea
 * @returns Precision level and color based on completeness
 */
export function analyzeTextPrecision(text: string): {
  level: 'exact' | 'street' | 'district' | 'city' | 'unknown';
  score: number; // 0-5
  color: string;
  message: string;
} {
  if (!text || text.trim().length === 0) {
    return {
      level: 'unknown',
      score: 0,
      color: '#bca79d', // Beige - incomplet
      message: 'Aucune adresse'
    };
  }

  const trimmedText = text.trim();

  // Check for street number at the beginning (e.g., "12 Rue...", "12, Rue...", "12bis Rue...")
  const hasStreetNumber = /^\d+[\s,]/.test(trimmedText) || /^\d+[a-zA-Z]?\s/.test(trimmedText);

  // Check for postal code (French: 5 digits, or other patterns)
  const hasPostalCode = /\b\d{5}\b/.test(trimmedText) || /\b\d{4,5}\b/.test(trimmedText);

  // Check for common street indicators
  const hasStreetName = /\b(rue|avenue|boulevard|place|chemin|allée|impasse|passage|route|cours|quai|square|street|road|av\.|bd\.|pl\.)\b/i.test(trimmedText);

  // Check minimum length for a reasonable address
  const hasReasonableLength = trimmedText.length >= 15;

  // Calculate score and determine level
  if (hasStreetNumber && hasStreetName && hasPostalCode) {
    return {
      level: 'exact',
      score: 5,
      color: '#00aa9f', // Turquoise - complet
      message: 'Adresse complète ✓'
    };
  } else if (hasStreetName && hasPostalCode && !hasStreetNumber) {
    return {
      level: 'street',
      score: 4,
      color: '#00745e', // Vert foncé - presque complet
      message: 'Ajoute un numéro de rue si applicable'
    };
  } else if (hasStreetName || (hasPostalCode && hasReasonableLength)) {
    return {
      level: 'district',
      score: 3,
      color: '#e1723c', // Orange - à compléter
      message: 'Précise la rue et le code postal'
    };
  } else if (hasReasonableLength) {
    return {
      level: 'city',
      score: 2,
      color: '#e1723c', // Orange - à compléter
      message: 'Ajoute plus de détails (rue, code postal)'
    };
  } else {
    return {
      level: 'unknown',
      score: 1,
      color: '#bca79d', // Beige - incomplet
      message: 'Adresse trop courte'
    };
  }
}
